import { IonItem, IonLabel, IonList, IonText } from "@ionic/react";
import { DateTime } from "luxon";
import { getBonusDetails } from "src/utils/bonus-pay";
import { Colors, Icon } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { centsToDollars, formatPay, msg, RequestData } from "~utils";

import "./ModalPayBreakdown.css";

export type ModalPayBreakdownProps = {
  payment: RequestData["payment"];
  isMultiDay: boolean;
  isMultiWeek: boolean;
  onDismiss: () => void;
};

export function ModalPayBreakdown(props: ModalPayBreakdownProps) {
  const { payment, isMultiWeek, isMultiDay, onDismiss } = props;

  return (
    <div className="modal-wrapper" css={{ paddingTop: 0, paddingBottom: 0 }}>
      <div className="modal-header">
        <h2 className="modal-header-label">{msg("PAY_BREAKDOWN_LABEL")}</h2>
        {/* FIXME: when design system button is available */}
        <button onClick={onDismiss} css={{ backgroundColor: "transparent" }}>
          <Icon name="Close" color={Colors.blue500} />
        </button>
      </div>
      <IonList lines="full" css={{ paddingTop: 0, paddingBottom: 0 }}>
        {PayBreakdownBody({ payment, isMultiDay, isMultiWeek })}
      </IonList>
      <a
        target="_blank"
        rel="noreferrer"
        href={generateSwingSupportURL(
          payment?.showHourlyRate
            ? msg("SWING_SUPPORT_LINK_PAY_W2")
            : msg("SWING_SUPPORT_LINK_PAY_1099"),
        )}
        className="modal-icon-link"
      >
        <span css={{ paddingRight: "8px" }} aria-hidden="true">
          <Icon name="External Link" color={Colors.blue500} />
        </span>
        <IonLabel className="modal-footer-label">{msg("PAY_BREAKDOWN_FOOTER")}</IonLabel>
      </a>
    </div>
  );
}

type PayBreakdownBodyProps = Omit<ModalPayBreakdownProps, "onDismiss">;

export function PayBreakdownBody(props: PayBreakdownBodyProps) {
  const { payment, isMultiWeek } = props;
  const payDateLabel = isMultiWeek ? `${msg("LABEL_NEXT_PAY_DATE")}:` : `${msg("LABEL_PAY_DATE")}:`;
  const payDate = DateTime.fromISO(payment?.paymentDate ?? "");
  const { bonusHourlyPay } = getBonusDetails(payment);
  const hourlyPayValue = bonusHourlyPay ?? payment?.hourlyPayValue ?? payment?.payValue ?? 0;

  return (
    <div>
      {payment?.showHourlyRate ? (
        <>
          <RowItem label={`${msg("LABEL_HOURLY")}:`} content={centsToDollars(hourlyPayValue)} />
          <RowItem
            label={`${msg("LABEL_ESTIMATED_TOTAL")}:`}
            content={formatPay(false, payment?.totalPayValue ?? 0)}
          />
        </>
      ) : payment?.isHourly ? (
        <RowItem
          label={`${msg("LABEL_ESTIMATED_TOTAL")}:`}
          content={formatPay(false, payment?.totalPayValue ?? 0)}
        />
      ) : (
        <>
          <RowItem
            label={`${msg("LABEL_TOTAL")}:`}
            content={centsToDollars(payment?.totalPayValue ?? payment?.payValue ?? 0)}
          />
          {payment?.fullDayPayValue ? (
            <RowItem
              label={`${msg("LABEL_FULL_DAY")}:`}
              content={centsToDollars(payment.fullDayPayValue)}
            />
          ) : null}
          {payment?.halfDayPayValue ? (
            <RowItem
              label={`${msg("LABEL_HALF_DAY")}:`}
              content={centsToDollars(payment.halfDayPayValue)}
            />
          ) : null}
        </>
      )}
      <RowItem label={payDateLabel} content={payDate.toFormat("ccc',' LLL dd")} />
      {isMultiWeek && (
        <IonItem css={{ fontSize: "var(--swing-font-sm)" }}>{msg("PAY_DATE_DISCLAIMER")}</IonItem>
      )}
    </div>
  );
}

function RowItem({ label, content }: { label: string; content: string | number }) {
  return (
    <IonItem css={{ "--min-height": "40px", "--padding-end": "var(--spacing-lg)" }}>
      <div
        css={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          columnGap: "var(--spacing-xl)",
          fontSize: "var(--swing-font-sm)",
          whiteSpace: "nowrap",
        }}
      >
        <IonText>{label}</IonText>
        <IonText>{content}</IonText>
      </div>
    </IonItem>
  );
}
